import { inject, Service } from '@angular/core';
import {SelectionSystemType} from '../model/selection-system-type';
import {Observable, Subject} from 'rxjs';
import {SureFlowLoopInitial} from '../model/sure-flow-loop-initial';
import {MatDialog} from '@angular/material/dialog';
import {AddSureFlowLoopDialogComponent} from '../dialogs/add-sure-flow-loop-dialog/add-sure-flow-loop-dialog.component';
import {AddNonSureFlowUnitDialogComponent} from '../dialogs/add-non-sure-flow-unit-dialog/add-non-sure-flow-unit-dialog.component';
import {SureFlowUnitData} from '../model/sure-flow-unit-data';
import {SureFlowAutoSelectInput} from '../model/sure-flow-auto-select-input';
import {AutoSelectSureFlowUnitsDialogComponent} from '../dialogs/auto-select-sure-flow-units-dialog/auto-select-sure-flow-units-dialog.component';
import {RearrangeSureFlowUnitsDialogComponent} from '../dialogs/rearrange-sure-flow-units-dialog/rearrange-sure-flow-units-dialog.component';
import {BulkEditSureFlowUnitsDialogComponent} from '../dialogs/bulk-edit-sure-flow-units-dialog/bulk-edit-sure-flow-units-dialog.component';
import {RatingUnit} from '../model/rating-unit';
import {WorkingSureFlowUnit} from '../model/working-sure-flow-unit';
import {WorkingSureFlowLoop} from '../model/working-sure-flow-loop';
import {AddSureFlowLoopsToScheduleDialogComponent} from '../dialogs/add-sure-flow-loops-to-schedule-dialog/add-sure-flow-loops-to-schedule-dialog.component';
import {SureFlowScheduleCreationInput} from '../model/sure-flow-schedule-creation-input';
import {TagInformation} from '../model/tag-information';
import {TagManagementDialogComponent} from '../configurator/common/tagging-dialog/tag-management-dialog.component';
import {RatingsTermsDialogComponent} from '../dialogs/ratings-terms-dialog/ratings-terms-dialog.component';
import {RatingsModel} from '../model/ratings-model';
import {SelectProductDialogComponent} from '../dialogs/select-product-dialog/select-product-dialog.component';
import {ConfigurationMessagesDialogComponent} from '../configurator/common/configuration-messages-dialog/configuration-messages-dialog.component';
import {ConfirmationDialogComponent} from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import {Entity} from "../entity/entity";
import {EntitySaveResponse} from "../entity/entity-save-response";
import {PartsDialogComponent} from "../dialogs/parts-dialog/parts-dialog.component";
import {WarrantyHelpDialogComponent} from "../dialogs/warranty-help-dialog/warranty-help-dialog.component";
import {AddScheduleDialogComponent} from "../dialogs/add-schedule-dialog/add-schedule-dialog.component";
import {RatingsMassUpdate} from "../model/ratings-mass-update";
import {RatingsMassUpdateDialogComponent} from "../dialogs/ratings-mass-update-dialog/ratings-mass-update-dialog.component";
import {MassUpdateErrorsComponent} from "../dialogs/mass-update-errors/mass-update-errors.component";
import {ColumnSelectionDialogComponent} from "../dialogs/column-selection-dialog/column-selection-dialog.component";
import {SelectionItem} from "../selections/selection-item";
import {ScheduleLineInput} from "../model/schedule-line-input";
import {WorkingUnitSelectionDialogComponent} from "../dialogs/working-unit-selection-dialog/working-unit-selection-dialog.component";
import {ScheduleSharingData} from "../model/schedule-sharing-data";
import {ScheduleSharingInput} from "../model/schedule-sharing-input";
import {ScheduleSharingDialogComponent} from "../dialogs/schedule-sharing-dialog/schedule-sharing-dialog.component";
import {Part} from '../products/part';
import {PartsSerialNumberDialogComponent} from '../dialogs/parts-serialnumber-dialog/parts-serialnumber-dialog.component';
import {EntityHistoryDialogComponent} from "../dialogs/entity-history-dialog/entity-history-dialog.component";
import {BulkUploadDialogComponent} from "../dialogs/bulk-upload-dialog/bulk-upload-dialog.component";
import { UnrestrictedEntityChangesDialogComponent } from '../dialogs/unrestricted-entity-changes-dialog/unrestricted-entity-changes-dialog.component';
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';
import { IecSchedulingData } from '../model/iec-scheduling-data';
import { SchedulingDataDialogComponent } from '../dialogs/scheduling-data-dialog/scheduling-data-dialog.component';

@Service()
export class DialogService {
  private readonly dialog = inject(MatDialog);


  showAddSureFlowLoopDialog(systemTypes: SelectionSystemType[]): Observable<SureFlowLoopInitial> {
    const subject: Subject<SureFlowLoopInitial> = new Subject<SureFlowLoopInitial>();
    const inputDialog = this.dialog.open(AddSureFlowLoopDialogComponent, {
      height: '560px',
      width: '620px',
      data: {
        systemTypes: systemTypes
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (loop: SureFlowLoopInitial) => subject.next(loop),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showAddNonSureFlowUnitDialog(systemTypeCode: string, unitData: SureFlowUnitData, showTagInput: boolean = true): Observable<SureFlowUnitData> {
    const subject: Subject<SureFlowUnitData> = new Subject<SureFlowUnitData>();
    const inputDialog = this.dialog.open(AddNonSureFlowUnitDialogComponent, {
      height: '440px',
      width: '520px',
      data: {
        systemTypeCode: systemTypeCode,
        unitData: unitData,
        showTagInput: showTagInput,
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (input) => subject.next(input),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showAutoSelectSureFlowUnitsDialog(input: SureFlowAutoSelectInput, allUnitIds: number[]): Observable<SureFlowAutoSelectInput> {
    const subject: Subject<SureFlowAutoSelectInput> = new Subject<SureFlowAutoSelectInput>();
    if ((allUnitIds?.length || 0) === 0) {
      subject.error('Please provide a list of all loop units in order to auto-select');
    }
    const inputDialog = this.dialog.open(AutoSelectSureFlowUnitsDialogComponent, {
      height: '360px',
      width: '520px',
      data: {
        isCooling: input.cooling,
        loopDetail: input.loopDetail,
        selectedUnitIds: input.selectedUnitIds,
        allUnitIds: allUnitIds,
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (input) => subject.next(input),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showRearrangeSureFlowUnitsDialog(units: SureFlowUnitData[]): Observable<SureFlowUnitData[]> {
    const subject: Subject<SureFlowUnitData[]> = new Subject<SureFlowUnitData[]>();
    const inputDialog = this.dialog.open(RearrangeSureFlowUnitsDialogComponent, {
      height: '520px',
      width: '520px',
      data: {
        units: units
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (rearranged: SureFlowUnitData[]) => subject.next(rearranged),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showBulkEditSureFlowUnitsDialog(systemType: string, representative: SureFlowUnitData, ratingUnit: RatingUnit, isMetric: boolean = false): Observable<WorkingSureFlowUnit> {
    const subject: Subject<WorkingSureFlowUnit> = new Subject<WorkingSureFlowUnit>();
    const inputDialog = this.dialog.open(BulkEditSureFlowUnitsDialogComponent, {
      height: '520px',
      width: '840px',
      data: {
        systemTypeCode: systemType,
        unitData: representative,
        ratingUnit: ratingUnit,
        metricInput: isMetric,
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (updated: WorkingSureFlowUnit) => subject.next(updated),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showAddSureFlowLoopsToScheduleDialog(loops: WorkingSureFlowLoop[]): Observable<SureFlowScheduleCreationInput> {
    const subject: Subject<SureFlowScheduleCreationInput> = new Subject<SureFlowScheduleCreationInput>();
    const inputDialog = this.dialog.open(AddSureFlowLoopsToScheduleDialogComponent, {
      height: '640px',
      width: '840px',
      data: {loops: loops}
    });
    inputDialog.afterClosed().subscribe({
      next: (schedule: SureFlowScheduleCreationInput) => subject.next(schedule),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showTagManagementDialog(currentTags: TagInformation[], tableHeight: number, quantity: number, preventQuantityChanges: boolean): Observable<TagInformation[]> {
    const subject: Subject<TagInformation[]> = new Subject<TagInformation[]>();
    const taggingDialog = this.dialog.open(TagManagementDialogComponent, {
      data: {
        tags: currentTags || [],
        quantity: quantity,
        tableHeight: tableHeight,
        preventQuantityChanges: preventQuantityChanges
      },
      disableClose: true,
    });
    taggingDialog.afterClosed().subscribe({
      next: (tags: TagInformation[]) => subject.next(tags),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showRatingsTermsDialog(): Observable<boolean> {
    const subject: Subject<boolean> = new Subject<boolean>();
    const termsDialog = this.dialog.open(RatingsTermsDialogComponent, {
      width: '1064px',
      height: '660px',
      disableClose: true,
    });
    termsDialog.afterClosed().subscribe({
      next: (response: boolean) => subject.next(response),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showProductSelectionDialog(products: RatingsModel[]): Observable<RatingsModel> {
    const subject: Subject<RatingsModel> = new Subject<RatingsModel>();
    const productDialog = this.dialog.open(SelectProductDialogComponent, {
      width: '1064px',
      height: '640px',
      data: {products: products}
    });
    productDialog.afterClosed().subscribe({
      next: (response: RatingsModel) => subject.next(response),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showConfiguratorMessagesDialog(messages: string[]) {
    if (messages && messages.length > 0) {
      messages = messages.filter((value, index, self) => self.indexOf(value) === index);
    }
    this.dialog.open(ConfigurationMessagesDialogComponent, {
      height: '540px',
      width: '790px',
      data: {messages: messages},
    });
  }

  showConfirmationDialog(messages: string[], acceptButtonText: string, cancelButtonText: string): Observable<boolean> {
    const subject: Subject<boolean> = new Subject<boolean>();
    const confirmationDialog = this.dialog.open(ConfirmationDialogComponent, {
      data: {messages: messages, acceptButtonText: acceptButtonText, cancelButtonText: cancelButtonText},
    });
    confirmationDialog.afterClosed().subscribe({
      next: (result: boolean) => subject.next(result),
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  showPartsDialog(entity: Entity, appEngineerSnBypass: boolean = false): Observable<Entity> {
    const entityCopy = entity == null ? null : JSON.parse(JSON.stringify(entity));
    const subject: Subject<Entity> = new Subject<Entity>();
    const partsDialog = this.dialog.open(PartsDialogComponent, {
      disableClose: true,
      width: '840px',
      height: '720px',
      data: {entity: entityCopy, appEngineerSnBypass: appEngineerSnBypass},
    });
    partsDialog.afterClosed().subscribe({
      next: (result) => subject.next(result),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showPartsHelpDialog(): void {
    this.dialog.open(WarrantyHelpDialogComponent, {
      width: '600px',
      height: '670px',
      data: {},
    });
  }

  showAddScheduleDialog(): Observable<Entity> {
    const subject: Subject<Entity> = new Subject<Entity>();
    const inputDialog = this.dialog.open(AddScheduleDialogComponent, {
      height: '360px',
      width: '640px',
      data: {},
    });
    inputDialog.afterClosed().subscribe({
      next: (input: Entity) => subject.next(input),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showRatingsMassUpdateDialog(updateData: RatingsMassUpdate): Observable<RatingsMassUpdate> {
    const subject: Subject<RatingsMassUpdate> = new Subject<RatingsMassUpdate>();
    const inputDialog = this.dialog.open(RatingsMassUpdateDialogComponent, {
      height: '520px',
      width: '850px',
      data: {updateData: updateData},
    });
    inputDialog.afterClosed().subscribe({
      next: (response: RatingsMassUpdate) => subject.next(response),
      error: (error) => subject.error(error),
    });
    return subject.asObservable();
  }

  showRatingMassUpdateErrorsDialog(units: RatingUnit[], scheduleLineInputs: ScheduleLineInput[]): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    const inputDialog = this.dialog.open(MassUpdateErrorsComponent, {
      height: '540px',
      width: '900px',
      data: {ratingUnits: units, scheduleLineInputs: scheduleLineInputs},
    });
    inputDialog.afterClosed().subscribe({
      next: () => subject.next(),
      error: (error) => subject.error(error),
    });
    return subject.asObservable();
  }

  showColumnSelectionDialog(allColumns: SelectionItem[], selectedColumns: string[]): Observable<string[]> {
    const subject: Subject<string[]> = new Subject<string[]>();
    const inputDialog = this.dialog.open(ColumnSelectionDialogComponent, {
      height: '620px',
      width: '1120px',
      data: {columnNames: allColumns, selectedColumnNames: selectedColumns},
    });
    inputDialog.afterClosed().subscribe({
      next: (columnNames: string[]) => subject.next(columnNames),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showWorkingUnitSelectionDialog(units: RatingUnit[]): Observable<RatingUnit[]> {
    const subject: Subject<RatingUnit[]> = new Subject<RatingUnit[]>();
    const inputDialog = this.dialog.open(WorkingUnitSelectionDialogComponent, {
      height: '600px',
      width: '800px',
      data: {units: units},
    });
    inputDialog.afterClosed().subscribe({
      next: (units: RatingUnit[]) => subject.next(units),
      error: (error) => subject.error(error),
    });
    return subject.asObservable();
  }

  showScheduleSharingDialog(sharingData: ScheduleSharingData): Observable<ScheduleSharingInput> {
    const subject: Subject<ScheduleSharingInput> = new Subject<ScheduleSharingInput>();
    const inputDialog = this.dialog.open(ScheduleSharingDialogComponent, {
      height: '380px',
      width: '540px',
      data: {
        user: sharingData.user,
        scheduleId: sharingData.scheduleId,
        organizations: sharingData.organizations,
        officeLabel: sharingData.officeLabel,
        userLabel: sharingData.userLabel,
        notificationLabel: sharingData.notificationLabel
      }
    });
    inputDialog.afterClosed().subscribe({
      next: (sharingInput: ScheduleSharingInput) => subject.next(sharingInput),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showPartsSerialNumberDialog(parts: Part[]): Observable<Part[]> {
    const subject: Subject<Part[]> = new Subject<Part[]>();
    const partsSerialNumberDialog = this.dialog.open(PartsSerialNumberDialogComponent, {
      disableClose: true,
      width: '840px',
      height: '720px',
      data: {parts: parts},
    });
    partsSerialNumberDialog.afterClosed().subscribe({
      next: (result) => subject.next(result),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  showEntityHistoryDialog(entity: Entity): void {
    this.dialog.open(EntityHistoryDialogComponent, {
      data: {entity: entity},
    });
  }

  showImportQuoteLinesDialog(entity: Entity, onUploadComplete?: (response: EntitySaveResponse) => void): Observable<EntitySaveResponse> {
    const entityCopy = entity == null ? null : JSON.parse(JSON.stringify(entity));
    const subject: Subject<EntitySaveResponse> = new Subject<EntitySaveResponse>();
    const importDialog = this.dialog.open(BulkUploadDialogComponent, {
      disableClose: true,
      width: '700px',
      height: '680px',
      data: {entity: entityCopy, onUploadComplete: onUploadComplete},
    });
    importDialog.afterClosed().subscribe({
      next: (response: EntitySaveResponse | null) => subject.next(response ?? undefined!),
      error: (error) => subject.error(error),
    });
    return subject.asObservable();
  }

  showUnrestrictedEntityUpdatesDialog(entity: Entity): Observable<UnrestrictedEntityUpdate> {
    const subject: Subject<UnrestrictedEntityUpdate> = new Subject<UnrestrictedEntityUpdate>();
    const dialog = this.dialog.open(UnrestrictedEntityChangesDialogComponent, {
      disableClose: true,
      data: { entity: entity },
    });
    dialog.afterClosed().subscribe({
      next: (result: UnrestrictedEntityUpdate) => subject.next(result),
      error: (error) => subject.error(error),
    });
    return subject.asObservable();
  }

  showSchedulingDataDialog(schedulingData: IecSchedulingData): void {
    this.dialog.open(SchedulingDataDialogComponent, { data: schedulingData });
  }

}
